import React, { useState, useEffect, useRef } from 'react'
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary'
import {
  // safeAccess,
  isValidFileObject,
  sanitizeFileObject,
} from '../../../../../utils/fileUtils/safeFileOperations'

const SafeOfficePreview = ({ file, fileType }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [currentViewer, setCurrentViewer] = useState('office365')
  const iframeRef = useRef(null)

  // Sanitize file object
  const safeFile = file

  // Office 365 Online Viewer
  const getOffice365ViewerUrl = (fileUrl) => {
    try {
      if (!fileUrl || typeof fileUrl !== 'string') return null
      const encodedUrl = encodeURIComponent(fileUrl)
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`
    } catch (error) {
      console.error('Error generating Office 365 URL:', error)
      return null
    }
  }

  // Google Docs Viewer (fallback)
  const getGoogleViewerUrl = (fileUrl) => {
    try {
      if (!fileUrl || typeof fileUrl !== 'string') return null
      const encodedUrl = encodeURIComponent(fileUrl)
      return `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`
    } catch (error) {
      console.error('Error generating Google Docs URL:', error)
      return null
    }
  }

  const getCurrentViewerUrl = () => {
    try {
      if (!safeFile?.previewLink) return null

      switch (currentViewer) {
        case 'office365':
          return getOffice365ViewerUrl(safeFile.previewLink)
        case 'google':
          return getGoogleViewerUrl(safeFile.previewLink)
        default:
          return getOffice365ViewerUrl(safeFile.previewLink)
      }
    } catch (error) {
      console.error('Error generating viewer URL:', error)
      return null
    }
  }

  const handleIframeLoad = () => {
    try {
      setLoading(false)
      setError(false)
    } catch (error) {
      console.error('Error handling iframe load:', error)
    }
  }

  const handleIframeError = () => {
    try {
      console.warn(`${currentViewer} viewer failed, trying fallback`)

      if (currentViewer === 'office365') {
        setCurrentViewer('google')
        setLoading(true)
        setError(false)
      } else {
        setError(true)
        setLoading(false)
      }
    } catch (error) {
      console.error('Error handling iframe error:', error)
      setError(true)
      setLoading(false)
    }
  }

  const viewerUrl = getCurrentViewerUrl()

  // Reset viewer when file changes
  useEffect(() => {
    try {
      if (safeFile) {
        setCurrentViewer('office365')
        setLoading(true)
        setError(false)
      }
    } catch (error) {
      console.error('Error in useEffect:', error)
      setError(true)
      setLoading(false)
    }
  }, [safeFile])

  if (!safeFile) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 bg-gray-50">
        <div className="text-6xl mb-4">📄</div>
        <h3 className="text-xl font-semibold mb-2">No Valid File</h3>
        <p className="text-gray-600">Please select a valid file to preview</p>
      </div>
    )
  }

  if (!viewerUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 bg-gray-50">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-xl font-semibold mb-2">Preview Not Available</h3>
        <p className="text-gray-600 mb-4">
          Cannot generate preview URL for this file
        </p>
        <button
          onClick={() => {
            try {
              if (safeFile?.previewLink) {
                const link = document.createElement('a')
                link.href = safeFile.previewLink
                link.download = safeFile.name || 'download'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
              }
            } catch (error) {
              console.error('Error downloading file:', error)
            }
          }}
          className="px-4 py-2 bg-[#3d6be0] text-white rounded-lg hover:bg-blue-600"
        >
          Download File
        </button>
      </div>
    )
  }

  return (
    <div className="w-full h-full relative bg-white">
      {/* Loading State */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3d6be0] mb-4"></div>
            <p className="text-gray-600">
              Loading {currentViewer === 'office365' ? 'Office 365' : 'Google'}{' '}
              viewer...
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {safeFile?.name || 'Unknown file'}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold mb-2">Preview Error</h3>
            <p className="text-gray-600 mb-4">
              Unable to load preview for this Office document.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => {
                  try {
                    setCurrentViewer('office365')
                    setLoading(true)
                    setError(false)
                  } catch (error) {
                    console.error('Error retrying:', error)
                  }
                }}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
              >
                Retry
              </button>
              <button
                onClick={() => {
                  try {
                    if (safeFile?.previewLink) {
                      const link = document.createElement('a')
                      link.href = safeFile.previewLink
                      link.download = safeFile.name || 'download'
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                    }
                  } catch (error) {
                    console.error('Error downloading file:', error)
                  }
                }}
                className="px-3 py-1 bg-[#3d6be0] text-white rounded text-sm hover:bg-blue-600"
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Viewer Controls */}
      <div className="absolute top-4 right-4 z-20 flex gap-2">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1 text-xs text-gray-600 border">
          {currentViewer === 'office365' ? 'Office 365' : 'Google Docs'}
        </div>
        <button
          onClick={() => {
            try {
              setCurrentViewer(
                currentViewer === 'office365' ? 'google' : 'office365'
              )
              setLoading(true)
              setError(false)
            } catch (error) {
              console.error('Error switching viewer:', error)
            }
          }}
          className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1 text-xs text-gray-600 border hover:bg-white"
        >
          Switch Viewer
        </button>
      </div>

      {/* Office Document Viewer */}
      <iframe
        ref={iframeRef}
        key={`${currentViewer}-${safeFile?.previewLink || 'no-url'}`}
        src={viewerUrl}
        className="w-full h-full border-0"
        title={`${safeFile?.name || 'Unknown file'} - ${currentViewer} viewer`}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
        referrerPolicy="no-referrer-when-downgrade"
        allow="fullscreen"
      />
    </div>
  )
}

export default function SafeOfficePreviewWithErrorBoundary(props) {
  return (
    <ErrorBoundary>
      <SafeOfficePreview {...props} />
    </ErrorBoundary>
  )
}
